import { ALL_RUN_CLUB_DATA } from '../assets/runClubDataIndex.js';

/**
 * Converts a club title to a URL-friendly slug
 * @param {string} title - The club title
 * @returns {string} - URL-friendly slug
 */
function titleToSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '') // Remove all spaces
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Converts a city name to a URL-friendly slug
 * @param {string} city - The city name
 * @returns {string} - URL-friendly city slug
 */
function cityToSlug(city) {
  return city
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Generates short URLs for all clubs, handling duplicates by appending city names
 * @returns {Object} - Map of short slug to club data
 */
export function generateClubSlugs() {
  const slugMap = new Map();
  const duplicateCheck = new Map();
  
  // First pass: identify potential duplicates by title
  ALL_RUN_CLUB_DATA.forEach(club => {
    const baseSlug = titleToSlug(club.title);
    if (duplicateCheck.has(baseSlug)) {
      duplicateCheck.get(baseSlug).push(club);
    } else {
      duplicateCheck.set(baseSlug, [club]);
    }
  });
  
  // Second pass: create final slugs with intelligent duplicate handling
  duplicateCheck.forEach((clubs, baseSlug) => {
    if (clubs.length === 1) {
      // No duplicates, use base slug
      const club = clubs[0];
      slugMap.set(baseSlug, {
        ...club,
        shortSlug: baseSlug,
        originalRoute: `/runclubs/${cityToSlug(club.city)}/${club.id}`
      });
    } else {
      // Group by city to handle same-city duplicates differently
      const citiesMap = new Map();
      clubs.forEach(club => {
        const citySlug = cityToSlug(club.city);
        if (citiesMap.has(citySlug)) {
          citiesMap.get(citySlug).push(club);
        } else {
          citiesMap.set(citySlug, [club]);
        }
      });
      
      citiesMap.forEach((cityClubs, citySlug) => {
        if (cityClubs.length === 1) {
          // Only one club with this name in this city - use club/city format
          const club = cityClubs[0];
          const uniqueSlug = `${baseSlug}/${citySlug}`;
          slugMap.set(uniqueSlug, {
            ...club,
            shortSlug: uniqueSlug,
            originalRoute: `/runclubs/${citySlug}/${club.id}`
          });
        } else {
          // Multiple clubs with same name in same city - add numbers
          cityClubs.forEach((club, index) => {
            const uniqueSlug = `${baseSlug}/${citySlug}/${index + 1}`;
            slugMap.set(uniqueSlug, {
              ...club,
              shortSlug: uniqueSlug,
              originalRoute: `/runclubs/${citySlug}/${club.id}`
            });
          });
        }
      });
    }
  });
  
  return Object.fromEntries(slugMap);
}

/**
 * Gets the short URL for a specific club
 * @param {string} clubId - The club ID
 * @returns {string|null} - Short URL slug or null if not found
 */
export function getClubShortSlug(clubId) {
  const slugs = generateClubSlugs();
  const club = Object.values(slugs).find(c => c.id === clubId);
  return club ? club.shortSlug : null;
}

/**
 * Gets club data by short slug
 * @param {string} shortSlug - The short slug
 * @returns {Object|null} - Club data or null if not found
 */
export function getClubByShortSlug(shortSlug) {
  const slugs = generateClubSlugs();
  return slugs[shortSlug] || null;
}

/**
 * Generates a list of all short URLs for debugging/testing
 * @returns {Array} - Array of {shortSlug, title, city, originalRoute}
 */
export function getAllShortUrls() {
  const slugs = generateClubSlugs();
  return Object.values(slugs).map(club => ({
    shortSlug: club.shortSlug,
    title: club.title,
    city: club.city,
    originalRoute: club.originalRoute,
    shortUrl: `/${club.shortSlug}`
  }));
} 